// P1-015: Single hardware item card
import { HardwareType } from '@/shared/types/common';
import type { HardwareItem, HardwareConfig } from '../types/hardware-internal';

/** Maps hardware type to an icon image path */
function getTypeIconSrc(type: HardwareType): string {
  switch (type) {
    case HardwareType.CPU:    return '/assets/icons/hardware/icon_hw_cpu.webp';
    case HardwareType.GPU:    return '/assets/icons/hardware/icon_hw_gpu_high.webp';
    case HardwareType.TPU:    return '/assets/icons/hardware/icon_hw_tpu.webp';
    case HardwareType.COOLER: return '/assets/icons/hardware/icon_hw_cooler_air.webp';
    case HardwareType.PSU:    return '/assets/icons/hardware/icon_hw_psu_standard.webp';
    default:                  return '/assets/icons/hardware/icon_hw_cpu.webp';
  }
}

/** Format special effect text */
function formatEffect(effect: { type: string; value: number }): string {
  switch (effect.type) {
    case 'REDUCE_CD': return `技能CD-${effect.value}`;
    case 'REDUCE_OVERLOAD_THRESHOLD': return `过载阈值+${effect.value}%`;
    default: return effect.type;
  }
}

interface HardwareCardProps {
  /** Either a HardwareItem (owned) or HardwareConfig (shop) */
  item: HardwareItem | HardwareConfig;
  selected?: boolean;
  status?: 'installed' | 'inventory' | 'shop';
  onClick?: () => void;
}

export function HardwareCard({ item, selected, status, onClick }: HardwareCardProps) {
  const isConfig = !('instanceId' in item);
  const isPSU = 'isPSU' in item && item.isPSU;

  return (
    <div
      className={`hw-card ${selected ? 'hw-card--selected' : ''} ${status === 'installed' ? 'hw-card--installed' : ''}`}
      onClick={onClick}
    >
      {/* Header: type icon + name */}
      <div className="hw-card__header">
        <img className="hw-card__type-icon" src={getTypeIconSrc(item.type)} alt={item.type} />
        <span className="hw-card__name">{item.name}</span>
      </div>

      {/* Stats */}
      <div className="hw-card__stats">
        {item.compute > 0 && (
          <span className="hw-card__stat">
            <span className="hw-card__stat-label">算力:</span>
            <span className="hw-card__stat-value hw-card__stat-value--compute">+{item.compute}</span>
          </span>
        )}
        {isPSU ? (
          <span className="hw-card__stat">
            <span className="hw-card__stat-label">供电上限:</span>
            <span className="hw-card__stat-value hw-card__stat-value--power">{item.power}</span>
          </span>
        ) : item.power > 0 ? (
          <span className="hw-card__stat">
            <span className="hw-card__stat-label">功耗:</span>
            <span className="hw-card__stat-value hw-card__stat-value--power">{item.power}</span>
          </span>
        ) : null}
        {item.type === HardwareType.COOLER ? (
          <span className="hw-card__stat">
            <span className="hw-card__stat-label">散热:</span>
            <span className="hw-card__stat-value hw-card__stat-value--cooling">+{item.cooling}</span>
          </span>
        ) : item.cooling > 0 && item.type !== HardwareType.PSU ? (
          <span className="hw-card__stat">
            <span className="hw-card__stat-label">发热:</span>
            <span className="hw-card__stat-value hw-card__stat-value--power">{item.cooling}</span>
          </span>
        ) : null}
      </div>

      {/* Price */}
      <div className="hw-card__price">₣ {item.price.toLocaleString()}</div>

      {/* Special effect */}
      {item.specialEffect && (
        <span className="hw-card__effect">{formatEffect(item.specialEffect)}</span>
      )}

      {/* Status */}
      {status && !isConfig && (
        <span className={`hw-card__status hw-card__status--${status}`}>
          {status === 'installed' ? '[INSTALLED]' : status === 'inventory' ? '[INVENTORY]' : ''}
        </span>
      )}
    </div>
  );
}
